"use client"

import { badgeVariants } from "@/components/ui/badge"
import { STATE_TONE_STYLES } from "@/components/ui/conversation/thread-tones"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { OUTCOME_TONES } from "@/lib/conversation-quality/outcome-display"
import type { ThreadQuality, ThreadTone } from "@/lib/conversation/thread-header"
import { cn } from "@/lib/utils"
import { useQualityCopy } from "@/components/i18n/quality-copy-context"


/**
 * How the last judged session went, beside the conversation's own state badges.
 *
 * The badge is also the way to fix it: clicking the verdict to correct the verdict beats
 * hunting for an unrelated entry in the overflow menu. Where the operator may not correct
 * it, the same badge is not pressable.
 */
export function SessionOutcomeBadge({
  quality,
  onCorrect,
}: {
  quality: ThreadQuality
  onCorrect?: () => void
}) {
  const copy = useQualityCopy()
  const { OUTCOME_LABELS, ui: tUi } = copy

  // The flag is the point of the review queue, so it outranks the outcome's own tone.
  const tone: ThreadTone = quality.needsReview ? "warning" : OUTCOME_TONES[quality.outcome]
  const className = cn(
    badgeVariants({ variant: "outline" }),
    "whitespace-nowrap px-1.5 py-0 text-[10px]",
    STATE_TONE_STYLES[tone],
    onCorrect && "cursor-pointer hover:brightness-95"
  )
  const label = quality.needsReview
    ? tUi("check", { outcome: OUTCOME_LABELS[quality.outcome] })
    : OUTCOME_LABELS[quality.outcome]

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          {onCorrect ? (
            <button type="button" className={className} onClick={onCorrect}>
              <span key={label}>{label}</span>
            </button>
          ) : (
            <span key={label} className={className}>{label}</span>
          )}
        </TooltipTrigger>
        <TooltipContent className="max-w-xs space-y-1 text-xs">
          {describe(quality, copy).map((line) => (
            <p key={line}>{line}</p>
          ))}
          {onCorrect && <p key={tUi("clickToCorrect")} className="text-muted-foreground">{tUi("clickToCorrect")}</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** What the badge cannot fit: which session, who decided, and the parts of an escalation. */
function describe(quality: ThreadQuality, copy: ReturnType<typeof useQualityCopy>): string[] {
  const { ui: tUi, ESCALATION_CATEGORY_LABELS, HUMAN_OUTCOME_LABELS, OUTCOME_METRIC_DEFINITIONS } = copy
  const lines = [tUi('session', { number: quality.sessionNumber, source: tUi(quality.outcomeSource) })]
  if (quality.escalationCategory) lines.push(tUi('handedOver', { category: ESCALATION_CATEGORY_LABELS[quality.escalationCategory] }))
  if (quality.humanOutcome) lines.push(tUi('humanOutcome', { label: OUTCOME_METRIC_DEFINITIONS.handoverOutcomes.label, outcome: HUMAN_OUTCOME_LABELS[quality.humanOutcome] }))
  if (quality.needsReview) lines.push(tUi('waiting'))
  return lines
}
